import { isPointInPolygon } from "geolib";
import { makeId } from "helper/util";
import { uniq, flatten as _flatten, isEmpty, uniqWith, isEqual } from "lodash";
import moment from "moment";
import { STANDARD_PACKAGE_TYPE_NAMES } from "../../../constants/packageTypes";

export const initRoutes = () => {
  let routesOptimize = {};

  [...Array(10)].map((_, index) => {
    routesOptimize[`route_${index}`] = {
      display: true,
      address: "",
      name: `Route ${index + 1}`
      // capacity: {
      //   length: 0,
      //   weight: 0,
      //   volume: 0
      // }
    };
  });
  return routesOptimize;
};

export const handleRoutesOptimize = (order, numberRoutes = 20, routes = []) => {
  const nullRoutes = handleNullRoutes(order, numberRoutes, routes);
  return { ...nullRoutes };
};

export const cloneStartLocationToEndLocation = (fleet) => {
  const fleetTemp = Object.assign({}, fleet);
  Object.keys(fleetTemp).forEach((key) => {
    if (fleetTemp[key]?.start_location) {
      fleetTemp[key].end_location = fleetTemp[key].start_location;
    }
    if (fleetTemp[key]?.shift_start) delete fleetTemp[key]?.shift_start;
  });
  return fleetTemp;
};

export const deleteShiftStart = (fleet) => {
  const fleetTemp = Object.assign({}, fleet);
  Object.keys(fleetTemp).forEach((key) => {
    if (fleetTemp[key]?.shift_start) delete fleetTemp[key]?.shift_start;
  });
  return fleetTemp;
};

export const handleNullRoutes = (order, numberRoute = 20, originRoutes = []) => {
  let routes = {};
  let firstPickup = {};

  if (order?.pickups?.length) {
    firstPickup = {
      ...order.pickups[0]
    };
  }
  // const maxCapacity = order?.dataService?.service?.maxCapacityVehicle;
  for (let i = 1; i <= numberRoute; i++) {
    const name = `route_${i}`;
    const assignedName = `assigned_route_${i}`;
    let route = { locked: false };
    if (originRoutes.length > 0) {
      route = originRoutes.find((r) => r.id === name);
    }

    routes[name] = {
      type: [name, assignedName],
      // capacity: {
      //   weight: maxCapacity?.maxWeight,
      //   length: maxCapacity?.maxLength,
      //   volume: maxCapacity?.maxVolume
      // },
      start_location: {
        name: firstPickup?.address?.address,
        lat: firstPickup?.address?.lngLat[1],
        lng: firstPickup?.address?.lngLat[0]
      },
      shift_start: firstPickup.start || "08:00",
      shift_end: getShiftEnd(firstPickup.start),
      locked: !!route?.locked
    };
  }
  return routes;
};

export const handleExistingRoutesOptimize = (order, { id, locked }) => {
  const assignedName = `assigned_${id}`;
  const routes = {};

  // If route has no starting address, use first pickup location
  let startLat = order.startAddressLat;
  let startLng = order.startAddressLng;

  if (!startLat || !startLng) {
    // Find first pickup location from routeLocations
    const firstPickup = order.routeLocations?.find(
      (rl) => rl.location?.type === "pickup"
    );
    if (firstPickup) {
      startLat = firstPickup.location.lat;
      startLng = firstPickup.location.lng;
      console.log("===USING FIRST PICKUP AS START===", { lat: startLat, lng: startLng });
    }
  }

  routes[id] = {
    type: [id, assignedName],
    start_location: {
      name: order.startAddress,
      lat: startLat,
      lng: startLng
    },
    shift_start: order.shiftStart || "08:00",
    shift_end: getShiftEnd(order.shiftStart),
    locked: !!locked
  };
  return { ...routes };
};

/** Stop id format the VRP path and doSortOrderCallback both use: `${type}__${jobId}`. */
export const stopIdOf = (type, jobId) => `${type}__${jobId}`;

/**
 * Route metadata to hand an engine callback reducer, built from the draft's routes plus the
 * fleet that was actually sent to the engine.
 *
 * The reducers stamp `start_location` (and `locked`) from here onto the solved routes, and
 * everything downstream depends on it: the route header's start address, the map marker,
 * and — critically — the fleet the "Force Stops to Routes" button rebuilds for /vrp-long.
 *
 * The draft's own `routes` cannot be trusted for that on its own. A newly created order
 * carries `routes: {}` (saveManualDraft writes `{ ...newOrder.routes }`, which is `{}` before
 * the first solve) and `{}` is truthy, so a plain `newOrder.routes ? newOrder.routes :
 * input.fleet` picks the empty object and every start_location is lost. The next /vrp-long
 * then goes out with `start_location: { id: "route_1" }` — no name, no coordinates — which
 * the routing proxy rejects as `route_N invalid start_location` before it ever queues a job,
 * which is why nothing reached the engine and the draft hung on "optimizing" forever.
 *
 * Only the missing fields are filled in; the draft's own route fields always win.
 */
export const routesFromEngineInput = (routes = {}, fleet = {}) => {
  const tmpRoutes = routes || {};
  const tmpFleet = fleet || {};
  const keys = uniq([...Object.keys(tmpFleet), ...Object.keys(tmpRoutes)]);
  const merged = {};
  keys.forEach((key) => {
    const route = tmpRoutes[key] || {};
    const vehicle = tmpFleet[key] || {};
    merged[key] = {
      ...route,
      start_location: route.start_location || vehicle.start_location,
      locked: route.locked ?? vehicle.locked
    };
  });
  return merged;
};

/**
 * Groups jobs into routes for the "keep my order" modes.
 *
 * Prefers an explicit grouping supplied by the user (the "Prerouted Route ID" import column).
 * "Internal Route ID" is deliberately not used: clients fill it with their own tracking IDs.
 * When that is absent — a manually built order, or a file without the column — it falls back
 * to the same chunking the optimize path uses, so route counts stay familiar.
 *
 * Within a group, stops keep the order the user gave: an explicit "Stop Sequence" column if
 * present, otherwise the row order the import recorded as `position`, otherwise array order.
 */
export const groupJobsByRouteKey = (jobs = [], preferredNoOfStops = 15) => {
  const routeKeyOf = (job) =>
    job?.dropoff?.preroutedRouteId || job?.pickup?.preroutedRouteId;
  const grouped = jobs.every((job) => !!routeKeyOf(job));

  const groups = {};
  if (grouped) {
    // Number routes in the order their group is first seen, so route_1 is the first group
    // in the file rather than whatever the group label happens to sort as.
    const keyOrder = uniq(jobs.map(routeKeyOf));
    keyOrder.forEach((key, index) => {
      groups[`route_${index + 1}`] = jobs.filter((job) => routeKeyOf(job) === key);
    });
  } else {
    const perRoute = Math.max(Number(preferredNoOfStops) || 15, 1);
    jobs.forEach((job, index) => {
      const key = `route_${Math.floor(index / perRoute) + 1}`;
      groups[key] = groups[key] || [];
      groups[key].push(job);
    });
  }

  const sequenceOf = (job, fallback) => {
    const raw = job?.dropoff?.stopSequence ?? job?.pickup?.stopSequence;
    const explicit = Number(raw);
    if (Number.isFinite(explicit)) return explicit;
    const recorded = Number(job?.dropoff?.position ?? job?.pickup?.position);
    return Number.isFinite(recorded) ? recorded : fallback;
  };

  Object.keys(groups).forEach((key) => {
    groups[key] = groups[key]
      .map((job, index) => ({ job, sort: sequenceOf(job, index) }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ job }) => job);
  });

  return groups;
};

/**
 * Minutes past midnight for "6:30" / "06:30", or null for anything unparseable.
 * Mirrors FixedOrderRouteService.parseTime on the backend.
 */
const parseClock = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? "").trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/**
 * When a stop's time window opens, as "HH:mm".
 *
 * Accepts both shapes a stop reaches us in: the mapped `start` string that `mapDataAddress`
 * produces for jobs, and the raw `from_time` moment the create-order form holds for
 * `order.pickups`. Those two never overlap, which is why `handleNullRoutes` — which only
 * ever looks at `start` — silently falls back to 08:00 for a form-built order.
 */
const windowStartOf = (stop) => {
  if (!stop) return undefined;
  if (parseClock(stop.start) !== null) return stop.start;
  const fromTime = stop.from_time;
  if (!fromTime) return undefined;
  const asMoment = moment.isMoment(fromTime) ? fromTime : moment(fromTime);
  return asMoment.isValid() ? asMoment.format("HH:mm") : undefined;
};

/** The earliest window open across `stops`, as "HH:mm", or undefined if none has one. */
const earliestWindowStart = (stops = []) => {
  let earliest;
  let earliestMinutes = Infinity;
  stops.forEach((stop) => {
    const start = windowStartOf(stop);
    const minutes = parseClock(start);
    if (minutes !== null && minutes < earliestMinutes) {
      earliest = start;
      earliestMinutes = minutes;
    }
  });
  return earliest;
};

/**
 * Builds the fleet for a fixed sequence. Same shape as `handleNullRoutes`, plus the `order`
 * array of stop ids — the field the VRP engine reads (FleetVehicleDTO.order) and the one our
 * own fixed-order endpoint reads.
 *
 * All of the route's pickups come first, then its dropoffs in the user's sequence. That is
 * what the engine itself produces when jobs share a warehouse, and it keeps the vehicle from
 * driving back to the depot between drops.
 *
 * Every job keeps its own pickup entry — they are NOT merged here, because each job needs a
 * stop of its own to receive a sortNo and ETAs on the way to `POST /orders`. Co-located
 * pickups are instead charged for their service time once (`squash_durations` in the engine,
 * `squashDurations` in FixedOrderRouteService), which gives them identical arrival times so
 * `groupStops` renders them as the single pickup the user expects.
 */
export const buildFixedOrderFleet = (order, groups) => {
  const base = handleNullRoutes(order, Object.keys(groups).length || 1);
  const fleet = {};

  Object.keys(groups).forEach((routeKey, index) => {
    const jobs = groups[routeKey];
    const pickupIds = uniq(jobs.map((job) => stopIdOf("pickup", job.id)));
    const dropoffIds = jobs.map((job) => stopIdOf("dropoff", job.id));
    const template = base[routeKey] || base[`route_${index + 1}`] || {};

    // The first pickup of the route is where the vehicle starts, matching handleNullRoutes.
    const firstPickup = jobs[0]?.pickup;

    // Unlike the engine paths, nothing downstream re-derives this: the fixed-order endpoint
    // starts its clock at shift_start and only ever waits, never leaves early. So take the
    // earliest pickup window on the route — the vehicle departs a depot it is already at,
    // so this is also the arrival time at the first stop. Fall back to the order's own
    // pickups before the 08:00 default, so a route whose stops carry no window still
    // follows whatever the user entered on the create page.
    const shiftStart =
      earliestWindowStart(jobs.map((job) => job.pickup)) ||
      earliestWindowStart(order?.pickups) ||
      template?.shift_start ||
      "08:00";

    fleet[routeKey] = {
      ...template,
      type: [routeKey, `assigned_${routeKey}`],
      start_location: {
        name: firstPickup?.address || template?.start_location?.name,
        lat: firstPickup?.lat ?? template?.start_location?.lat,
        lng: firstPickup?.lng ?? template?.start_location?.lng
      },
      shift_start: shiftStart,
      shift_end: getShiftEnd(shiftStart),
      order: uniq([...pickupIds, ...dropoffIds])
    };
  });

  return fleet;
};

/**
 * Flat, one-entry-per-stop `visits` map. This is the VRP shape (keyed by stop id), which is
 * what both `/vrp-long` and our fixed-order endpoint expect — NOT the nested pickup/dropoff
 * shape `dataJobTypeOptimize` builds for `/pdp-long`.
 */
export const dataStopTypeFixedOrder = (jobs = []) => {
  const visits = {};
  jobs.forEach((job) => {
    ["pickup", "dropoff"].forEach((type) => {
      const stop = job[type];
      if (!stop) return;
      visits[stopIdOf(type, job.id)] = {
        location: { name: stop.address, lat: stop.lat, lng: stop.lng },
        start: stop.start,
        end: stop.end,
        duration: Number(stop.duration || 0)
      };
    });
  });
  return visits;
};

const getShiftEnd = (start = "08:00") => {
  const splitStart = start.split(":");
  const hour = parseInt(splitStart[0]);
  return hour + 12 > 24 ? "23:59" : `${hour + 12}:${splitStart[1]}`;
};

const mapDataPackage = (packs) => {
  return packs.map((pack) => {
    return {
      maxLength: pack.maxLength,
      maxWeight: pack.maxWeight,
      maxVolume: pack.maxVolume,
      name: pack.name,
      quantity: pack.quantity,
      packageTypeId: pack?.packageTypeId,
      barcode: pack?.barcode,
      poolToShiftPackages: pack?.packagesV2,
      inventoryItemId: pack?.inventoryItemId,
      inventoryItemSerials: pack?.inventoryItemSerials
    };
  });
};

export const mapDataAddress = (values, type, imagesAddressInfo) => {
  const images =
    (imagesAddressInfo[`${type}s`] && imagesAddressInfo[`${type}s`][values.id]) || [];
  return {
    id: values.id,
    address: values.address.address,
    lat: values.address?.lngLat?.length ? values.address.lngLat[1] : undefined,
    lng: values.address?.lngLat?.length ? values.address.lngLat[0] : undefined,
    locationName: values.location_name,
    physicalAddress: values.physical_address,
    contactName: values.contact_name,
    start: values.from_time
      ? typeof values.from_time === "object"
        ? values.from_time.format("HH:mm")
        : moment(values.from_time).format("HH:mm")
      : undefined,
    end: values.to_time
      ? typeof values.to_time === "object"
        ? values.to_time.format("HH:mm")
        : moment(values.to_time).format("HH:mm")
      : undefined,
    email: values.email,
    duration: values.duration,
    phoneList: values.phone_numbers,
    note: values.note,
    routeId: values?.routeId,
    type,
    files: images?.map((image) => image.id),
    internalRouteId: values.internalRouteId,
    // Used only by the "keep my order" modes: which route a stop belongs to, and its
    // sequence within that route.
    preroutedRouteId: values.preroutedRouteId,
    stopSequence: values.stopSequence,
    position: values.position,
    internalOrderId: values.internalOrderId,
    internalCustomerId: values.internalCustomerId,
    salesPerson: values.salesPerson,
    cod: values.cod
  };
};

export const getJobs = (order) => {
  const {
    packages,
    pickups,
    dropoffs,
    isAllPackage,
    isOptimizeRoute,
    imagesAddressInfo
  } = order;
  const jobs = [];
  const packagesByPick = {};
  packages.forEach((pack) => {
    packagesByPick[pack.pickupId] = packagesByPick[pack.pickupId] || [];
    packagesByPick[pack.pickupId].push(pack);
  });

  Object.keys(packagesByPick).map((pickId) => {
    const pick = pickups.find((p) => String(p.id) === String(pickId));

    if (isAllPackage) {
      const rs = {
        id: `job_${makeId(4)}`,
        pickup: {},
        dropoff: {},
        packages: []
      };
      const drop = dropoffs[0];

      rs.pickup = mapDataAddress(pick, "pickup", imagesAddressInfo);
      rs.dropoff = mapDataAddress(drop, "dropoff", imagesAddressInfo);
      rs.packages = mapDataPackage(packagesByPick[pickId]);

      jobs.push(rs);
    } else {
      let dropIds = [];
      packagesByPick[pickId].forEach((pack) => {
        pack.dropoffs?.length > 0 &&
          pack.dropoffs.forEach((drop) => dropIds.push(drop.id));
      });
      dropIds = uniq(dropIds);
      dropIds.forEach((dropId) => {
        const rs = {
          id: `job_${makeId(4)}`,
          pickup: {},
          dropoff: {},
          packages: []
        };
        const drop = dropoffs.find((d) => d.id === dropId);
        const packsByDrop = [];
        packagesByPick[pickId].forEach((pack) => {
          if (
            pack.dropoffs?.length &&
            pack.dropoffs.map((dr) => dr.id).includes(dropId)
          ) {
            const packDrop = pack.dropoffs.find((dro) => dro.id === dropId);
            packsByDrop.push({
              ...pack,
              quantity: packDrop.quantity
            });
          }
        });

        rs.pickup = mapDataAddress(pick, "pickup", imagesAddressInfo);
        rs.dropoff = mapDataAddress(drop, "dropoff", imagesAddressInfo);
        rs.packages = mapDataPackage(packsByDrop);
        rs.isOptimize = isOptimizeRoute;
        rs.isSamePackages = isAllPackage;
        jobs.push(rs);
      });
    }
  });
  return jobs;
};

export const dataJobTypeOptimize = (jobs) => {
  if (jobs.length === 0) {
    return {};
  }
  const data = {};
  jobs.map((job, index) => {
    data[job.id] = {
      pickup: {
        location: {
          name: job.pickup.address,
          lat: job.pickup.lat,
          lng: job.pickup.lng
        },
        start: job.pickup.start,
        end: job.pickup.end,
        duration: Number(job.pickup.duration)
      },
      dropoff: {
        location: {
          name: job.dropoff.address,
          lat: job.dropoff.lat + index * 0.00000000000001,

          lng: job.dropoff.lng
        },
        start: job.dropoff.start,
        end: job.dropoff.end,
        duration: Number(job.dropoff.duration)
      }
      // load: calculateLoad(job.packages)
    };
  });
  return data;
};

export const calculateLoad = (packages) => {
  let load = {
    // length: 0,
    // weight: 0,
    volume: 0
  };
  packages.map((pack) => {
    load = {
      // length: load.length + pack.maxLength * pack.quantity,
      // weight: load.volume + pack.maxWeight * pack.quantity,
      volume: load.volume + pack.maxVolume * pack.quantity
    };
  });
  return load;
};

export const dataJobsToLocations = (jobs) => {
  const locations = [];
  jobs.map((job) => {
    locations.push({
      jobId: job.id,
      color: job?.color,
      id: `${job.id}_pickup`,
      address: job.pickup.address,
      lat: job.pickup.lat,
      lng: job.pickup.lng,
      type: "pickup",
      internalDriverId: job.internalDriverId,
      start: job.pickup.start,
      end: job.pickup.end,
      duration: job.pickup.duration
    });
    locations.push({
      jobId: job.id,
      color: job?.color,
      id: `${job.id}_dropoff`,
      address: job.dropoff.address,
      lat: job.dropoff.lat,
      lng: job.dropoff.lng,
      type: "dropoff",
      internalDriverId: job.internalDriverId,
      start: job.dropoff.start,
      end: job.dropoff.end,
      duration: job.dropoff.duration
    });
  });
  return locations;
};

export function addPropertiesToPolygon(poligon) {
  if (!poligon?.length) return [];
  const flattenPoligon = _flatten(poligon);
  return flattenPoligon.map((item) => ({ longitude: item[0], latitude: item[1] }));
}

export function verifyPickupLocations(selectedService, pickups) {
  if (!selectedService?.pickupCoords?.length) {
    return pickups;
  }

  const outsidePickups = [];
  const pickupGeofence = addPropertiesToPolygon(selectedService?.pickupCoords);
  for (const pickup of pickups) {
    if (
      pickup?.lat &&
      pickup?.lng &&
      !isPointInPolygon({ lat: pickup.lat, lng: pickup.lng }, pickupGeofence)
    ) {
      outsidePickups.push(pickup);
    }
  }
  return outsidePickups;
}

export function verifyDropLocations(selectedService, dropoffs) {
  if (!selectedService?.pickupCoords?.length) {
    return dropoffs;
  }

  const outsideDropoffs = [];
  const deliveryGeofence = addPropertiesToPolygon(selectedService?.deliveryCoords);
  for (const dropoff of dropoffs) {
    if (
      dropoff?.lat &&
      dropoff?.lng &&
      !isPointInPolygon({ lat: dropoff.lat, lng: dropoff.lng }, deliveryGeofence)
    ) {
      outsideDropoffs.push(dropoff);
    }
  }
  return outsideDropoffs;
}

export function verifyPackages(selectedService, packages) {
  if (!selectedService?.packageTypes?.length) {
    return packages;
  }

  const notAcceptedPackageTypes = [];
  const acceptedPackageTypeIds = selectedService?.packageTypes?.map(
    (packageType) => packageType.id
  );
  for (const pack of packages) {
    if (!acceptedPackageTypeIds.includes(pack.packageTypeId)) {
      notAcceptedPackageTypes.push(pack);
    }
  }
  return notAcceptedPackageTypes;
}

/**
 * By contract, the return-distance rate is always billed - the "return to pickup" and
 * "return failed packages" toggles are compliance-only and never gate this charge. The
 * distance still goes through the Free / Short / Long tiers in calculatePricing, so a route
 * whose return leg is under the free threshold simply prices at $0, same as always.
 */
export function buildPricingEstimateBody(route, clientType) {
  const packagesPerRoute = getTotalPackagesPerRoute(route);

  const pickupsQuantity = [];
  const deliveriesQuantity = [];
  let totalManifestCount = 0;
  const manifestList = [];

  route.jobs.forEach((job) => {
    if (job?.pickup?.address) {
      pickupsQuantity.push({
        address: job.pickup.address,
        start: job.pickup.start
      });
    }

    if (job?.dropoff?.address) {
      deliveriesQuantity.push({
        address: job.dropoff.address,
        start: job.dropoff.start
      });
    }

    // Count total manifests (internal order IDs) from dropoff
    // Each manifest ID in the comma-separated list counts as 1, even if duplicated
    if (
      job?.dropoff?.internalOrderId &&
      typeof job.dropoff.internalOrderId === "string"
    ) {
      const dropoffManifests = job.dropoff.internalOrderId
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id);
      totalManifestCount += dropoffManifests.length;
      manifestList.push(...dropoffManifests);
    }
  });

  console.log(
    "Manifest count for pricing estimate:",
    totalManifestCount,
    "manifests:",
    manifestList
  );

  return {
    serviceId: route.serviceId,
    totalRouteTime: (route.workingMins / 60).toFixed(2),
    totalRouteDistance: parseFloat((route.distance / 1609.344).toFixed(2)),
    totalReturnDistance: parseFloat(((route.returnDistance || 0) / 1609.344).toFixed(2)),
    totalReturnTime: ((route.returnMins || 0) / 60).toFixed(2),
    totalMicroPackages: packagesPerRoute.totalMicroPackages,
    totalSmallPackages: packagesPerRoute.totalSmallPackages,
    totalMediumPackages: packagesPerRoute.totalMediumPackages,
    totalLargePackages: packagesPerRoute.totalLargePackages,
    totalCustomPackages: packagesPerRoute.totalCustomPackages,
    totalPalletPackages: packagesPerRoute.totalPalletPackages,
    totalPostalPackages: packagesPerRoute.totalPostalPackages,
    totalPickupStops: pickupsQuantity.length,
    totalDeliveryStops: deliveriesQuantity.length,
    totalReturnDeliveryStops: 1,
    vehicleCategoryId: route.vehicleCategory?.id,
    internalOrderIdsDropoff: totalManifestCount,
    organization: clientType === "organization"
  };
}

export function getTotalPackagesPerRoute(route) {
  let totalMicroPackages = 0;
  let totalSmallPackages = 0;
  let totalMediumPackages = 0;
  let totalLargePackages = 0;
  let totalCustomPackages = 0;
  let totalPalletPackages = 0;
  let totalPostalPackages = 0;

  for (let i = 0; i < route.jobs.length; i++) {
    const job = route.jobs[i];

    const microPackage = job.packages.find((item) => {
      return item.name === STANDARD_PACKAGE_TYPE_NAMES.MICRO;
    });
    const smallPackage = job.packages.find((item) => {
      return item.name === STANDARD_PACKAGE_TYPE_NAMES.SMALL;
    });
    const mediumPackage = job.packages.find((item) => {
      return item.name === STANDARD_PACKAGE_TYPE_NAMES.MEDIUM;
    });
    const largePackage = job.packages.find((item) => {
      return item.name === STANDARD_PACKAGE_TYPE_NAMES.LARGE;
    });
    const customPackage = job.packages.find((item) => {
      return item.name === STANDARD_PACKAGE_TYPE_NAMES.CUSTOM;
    });
    const palletPackage = job.packages.find((item) => {
      return item.name === STANDARD_PACKAGE_TYPE_NAMES.PALLET;
    });
    const postalPackage = job.packages.find((item) => {
      return (
        item.name === STANDARD_PACKAGE_TYPE_NAMES.POSTAL ||
        item.name?.startsWith("Postal")
      );
    });

    totalSmallPackages = totalSmallPackages + (smallPackage?.quantity || 0);
    totalMicroPackages = totalMicroPackages + (microPackage?.quantity || 0);
    totalMediumPackages = totalMediumPackages + (mediumPackage?.quantity || 0);
    totalLargePackages = totalLargePackages + (largePackage?.quantity || 0);
    totalCustomPackages = totalCustomPackages + (customPackage?.quantity || 0);
    totalPalletPackages = totalPalletPackages + (palletPackage?.quantity || 0);
    totalPostalPackages = totalPostalPackages + (postalPackage?.quantity || 0);
  }

  return {
    totalMicroPackages,
    totalSmallPackages,
    totalMediumPackages,
    totalLargePackages,
    totalCustomPackages,
    totalPalletPackages,
    totalPostalPackages
  };
}

export const routeEstimateDefault = {
  additionalTimeOnRoute: 0,
  basePrice: 0,
  bookingFee: 0,
  estimatePrice: 0,
  fuelSurcharge: 0,
  overDeliveryStops: 0,
  preSubTotal: 0,
  priceAdditionalInsurance: 0,
  priceAdditionalTimeOnRoute: 0,
  priceB2BTax: 0,
  priceCustomPackage: 0,
  priceDeliveryStops: 0,
  priceDistanceOnRoute: 0,
  priceFuelSurcharge: 0,
  priceLargePackage: 0,
  priceMediumPackage: 0,
  priceMicroPackage: 0,
  priceMinimumDeliveries: 0,
  priceMinimumEarnings: 0,
  priceOverDeliveryStops: 0,
  pricePalletPackage: 0,
  pricePickupStops: 0,
  priceReturnDeliveryStop: 0,
  priceReturnDistanceLong: 0,
  priceReturnDistanceShort: 0,
  priceReturnTime: 0,
  priceSalesTax: 0,
  priceSmallPackage: 0,
  priceTotalPackages: 0,
  returnDeliveryStop: 0,
  subTotal: 0,
  totalRouteTime: 0,
  volumePackageDiscounts: 0
};

export const filterValidRoutes = (routes = {}) => {
  const validRoutes = { ...routes };
  for (const routeKey of Object.keys(routes)) {
    if (!routes[routeKey]?.jobs?.length) {
      delete validRoutes[routeKey];
    }
  }
  return validRoutes;
};

export const getLastAddress = (route = {}) => {
  if (isEmpty(route || isEmpty(route.jobs))) return "Unknown";
  const { jobs } = route;
  return jobs[jobs.length - 1].dropoff?.address;
};
